// src/components/CampaignWizard/StepFallback.tsx
import React from "react";
import {
	Row,
	Col,
	Button,
	FormGroup,
	Label,
	Input,
	FormText,
	Card,
	CardBody,
	CardHeader,
} from "reactstrap";
import Select from "react-select";
import { ChannelType, CHANNEL_LABELS } from "./StepChannels";

export interface FallbackRule {
	fromChannel: ChannelType;
	onEvents: string[];
	toChannel: ChannelType;
	delayHours: number;
}

export interface StepFallbackProps {
	data: FallbackRule[];
	onChange: (d: FallbackRule[]) => void;
}

const EVENT_OPTIONS = [
	{ value: "BOUNCE", label: "Bounce" },
	{ value: "NOT_OPENED", label: "Not Opened" },
];

const StepFallback: React.FC<StepFallbackProps> = ({
	data,
	onChange
}) => {
	// add a new blank rule
	const addRule = () =>
		onChange([
			...data,
			{
				fromChannel: "EMAIL",
				onEvents: [],
				toChannel: "SMS",
				delayHours: 24,
			},
		]);

	// update one rule at index i
	const updateRule = (i: number, upd: Partial<FallbackRule>) => {
		const copy = data.slice();
		copy[i] = { ...copy[i], ...upd };
		onChange(copy);
	};

	// remove a rule
	const removeRule = (i: number) => onChange(data.filter((_, idx) => idx !== i));

	return (
		<>
			{data.map((r, i) => (
				<Card key={i} className="mb-3 shadow-sm">
					<CardHeader className="d-flex justify-content-between align-items-center">
						<strong>Fallback Rule {i + 1}</strong>
						<Button color="danger" size="sm" onClick={() => removeRule(i)}>
							&times;
						</Button>
					</CardHeader>
					<CardBody>
						<Row className="gx-3">
							<Col md={3}>
								<FormGroup>
									<Label>From</Label>
									<Input
										type="select"
										value={r.fromChannel}
										onChange={(e) =>
											updateRule(i, { fromChannel: e.target.value as ChannelType })
										}
									>
										{(Object.keys(CHANNEL_LABELS) as ChannelType[]).map((ch) => (
											<option key={ch} value={ch}>
												{CHANNEL_LABELS[ch]}
											</option>
										))}
									</Input>
								</FormGroup>
							</Col>

							<Col md={4}>
								<FormGroup>
									<Label>On Events</Label>
									<Select
										isMulti
										options={EVENT_OPTIONS}
										value={EVENT_OPTIONS.filter((opt) =>
											r.onEvents.includes(opt.value)
										)}
										onChange={(opts: any[]) =>
											updateRule(i, {
												onEvents: (opts as any[]).map((o) => o.value),
											})
										}
										placeholder="Select event(s)…"
									/>
									<FormText color="muted">
										Trigger fallback when these events occur.
									</FormText>
								</FormGroup>
							</Col>

							<Col md={3}>
								<FormGroup>
									<Label>To</Label>
									<Input
										type="select"
										value={r.toChannel}
										onChange={(e) =>
											updateRule(i, { toChannel: e.target.value as ChannelType })
										}
									>
										{/* don’t allow same channel as “From” */}
										{(Object.keys(CHANNEL_LABELS) as ChannelType[])
											.filter((ch) => ch !== r.fromChannel)
											.map((ch) => (
												<option key={ch} value={ch}>
													{CHANNEL_LABELS[ch]}
												</option>
											))}
									</Input>
								</FormGroup>
							</Col>

							<Col md={2}>
								<FormGroup>
									<Label>Delay (hrs)</Label>
									<Input
										type="number"
										min={0}
										value={r.delayHours}
										onChange={(e) =>
											updateRule(i, { delayHours: Number(e.target.value) })
										}
									/>
									<FormText color="muted">
										How long after the event to retry.
									</FormText>
								</FormGroup>
							</Col>
						</Row>
					</CardBody>
				</Card>
			))}

			<div className="mb-4">
				<Button color="primary" outline onClick={addRule}>
					+ Add Fallback Rule
				</Button>
			</div>
		</>
	);
};

export default StepFallback;
